import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import { ICity, ICityCollectionParams, ICreateCity, IUpdateCity } from 'entities/City/City.models';
import { cityTransport } from 'entities/City/City.transport';

interface IState {
  city: ICity | null;
  cityList: ICity[];
  cityCount: number;
  isCityLoading: boolean;
  cityError: string | null;
  detectedCity: ICity | null;
  cityParents: ICity[];
}

const initialState: IState = {
  city: null,
  cityList: [],
  cityCount: 0,
  isCityLoading: false,
  cityError: null,
  detectedCity: null,
  cityParents: [],
};

export const citySlice = createSlice({
  name: 'city',
  initialState,
  reducers: {
    setCity(state, action: PayloadAction<ICity | null>) {
      state.city = action.payload;
    },
    setCityList(state, action: PayloadAction<ICity[]>) {
      state.cityList = action.payload;
    },
    setCityCount(state, action: PayloadAction<number>) {
      state.cityCount = action.payload;
    },
    setIsCityLoading(state, action: PayloadAction<boolean>) {
      state.isCityLoading = action.payload;
    },
    setCityError(state, action: PayloadAction<string | null>) {
      state.cityError = action.payload;
    },
    addCityListItem(state, action: PayloadAction<ICity>) {
      state.cityList.push(action.payload);
    },
    patchCityListItem(state, action: PayloadAction<ICity>) {
      const city = state.cityList.find((i) => i.id === action.payload.id);

      if (!city) {
        return;
      }

      Object.assign(city, action.payload);
    },
    setDetectedCity(state, action: PayloadAction<ICity | null>) {
      state.detectedCity = action.payload;
    },
    setCityParents(state, action: PayloadAction<ICity[]>) {
      state.cityParents = action.payload;
    },
  },
});

export const {
  setCity,
  setCityList,
  setCityCount,
  setIsCityLoading,
  setCityError,
  addCityListItem,
  patchCityListItem,
  setDetectedCity,
  setCityParents,
} = citySlice.actions;

export const selectCity = (state: RootState) => state.city.city;
export const selectCityList = (state: RootState) => state.city.cityList;
export const selectCityCount = (state: RootState) => state.city.cityCount;
export const selectIsCityLoading = (state: RootState) => state.city.isCityLoading;
export const selectCityError = (state: RootState) => state.city.cityError;
export const selectDetectedCity = (state: RootState) => state.city.detectedCity;
export const selectCityParents = (state: RootState) => state.city.cityParents;

export const cityReducer = citySlice.reducer;

export const getCityList =
  (params: ICityCollectionParams) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setIsCityLoading(true));

    try {
      const response = await cityTransport.get(params);

      dispatch(setCityList(response.data));
      dispatch(setCityCount(response.count));
    } catch (error) {
      axiosErrorHandler(error);
    } finally {
      dispatch(setIsCityLoading(false));
    }
  };

export const getCityById =
  (id: number) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setIsCityLoading(true));

    try {
      const response = await cityTransport.getById(id);

      dispatch(setCity(response));
    } catch (error) {
      axiosErrorHandler(error);
    } finally {
      dispatch(setIsCityLoading(false));
    }
  };

export const createCity =
  ({ onSuccess, ...payload }: ICreateCity) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setCityError(null));
    dispatch(setIsCityLoading(true));

    try {
      const response = await cityTransport.create(payload);

      dispatch(setCity(response));
      dispatch(addCityListItem(response));
      onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error, setCityError);
    } finally {
      dispatch(setIsCityLoading(false));
    }
  };

export const updateCity =
  ({ onSuccess, ...payload }: IUpdateCity) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setCityError(null));
    dispatch(setIsCityLoading(true));

    try {
      const response = await cityTransport.update(payload);

      dispatch(setCity(response));
      dispatch(patchCityListItem(response));
      onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error, setCityError);
    } finally {
      dispatch(setIsCityLoading(false));
    }
  };

export const detectCity =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setIsCityLoading(true));

    try {
      const response = await cityTransport.detectCity();

      dispatch(setDetectedCity(response));
    } catch (error) {
      axiosErrorHandler(error);
    } finally {
      dispatch(setIsCityLoading(false));
    }
  };

export const getCityParents =
  (params: ICityCollectionParams) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setIsCityLoading(true));

    try {
      const response = await cityTransport.get({ ...params, withoutParent: true });

      dispatch(setCityParents(response.data));
    } catch (error) {
      axiosErrorHandler(error);
    } finally {
      dispatch(setIsCityLoading(false));
    }
  };
